import { parse } from 'tinyduration';
import {
  CONNECTION_STATUS,
  SENSOR_TYPES,
  COMPONENT_STATUS,
  SENSOR_ID_MAP
} from './enums';
// Deactivation duration constants
export const DEACTIVATION_DURATION_LONG = 'PT876000H';              // ~100 years - human stays in control during crisis
export const DEACTIVATION_DURATION_SHORT_PRESOAK = 'PT0S';         // 0 seconds - autonomous can re-engage after presoak
export const DEACTIVATION_DURATION_SHORT_ACTIVATION = 'PT10M';      // 10 minutes - autonomous can re-engage after activation
export const EMBER_SENSOR_ALERT_STATUS = COMPONENT_STATUS.ACTIVE;                  // EmberSensor status when detecting threat
export const EMBER_SENSOR_ID = SENSOR_ID_MAP[SENSOR_TYPES.EMBER];

// Deactivation types
export const DEACTIVATION_TYPE = {
  PRESOAK: 'presoak',
  ACTIVATION: 'activation'
};

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const HOURS_PER_DAY = 24;
const SECONDS_PER_DAY = HOURS_PER_DAY * SECONDS_PER_HOUR;
const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

/**
 * Custom error for failures parsing ISO 8601 duration strings in the priming countdown.
 */
export class PrimingDurationParseError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PrimingDurationParseError';
  }
}

/**
 * Parses an ISO 8601 duration string and returns the total number of whole seconds.
 * Unlike formatRemainingDuration, this performs no rounding and returns an integer.
 * @param {string} durationString - ISO 8601 duration string (e.g., "PT45S", "PT1M30S")
 * @returns {number} Total seconds as a non-negative integer
 * @throws {PrimingDurationParseError} If the input is not a valid ISO 8601 duration string
 */
export const parseDurationToSeconds = (durationString) => {
  if (!durationString || typeof durationString !== 'string') {
    throw new PrimingDurationParseError(`Invalid duration input: ${durationString}`);
  }
  let parsed;
  try {
    parsed = parse(durationString);
  } catch (e) {
    throw new PrimingDurationParseError(`Failed to parse ISO 8601 duration '${durationString}': ${e.message}`);
  }
  return Math.floor(
    (parsed.years || 0) * DAYS_PER_YEAR * SECONDS_PER_DAY +
    (parsed.months || 0) * DAYS_PER_MONTH * SECONDS_PER_DAY +
    ((parsed.weeks || 0) * DAYS_PER_WEEK + (parsed.days || 0)) * SECONDS_PER_DAY +
    (parsed.hours || 0) * SECONDS_PER_HOUR +
    (parsed.minutes || 0) * SECONDS_PER_MINUTE +
    (parsed.seconds || 0)
  );
};

/**
 * Formats a total seconds value as a human-readable countdown string.
 * @param {number|null} seconds - Total seconds remaining, or null if unavailable
 * @returns {string|null} Formatted string (e.g., "45s", "2m 30s", "1m") or null if seconds is null
 */
export const formatPrimingCountdown = (seconds) => {
  if (seconds === null) return null;
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/**
 * TODO: This method is for temporary logic to allow autonomy to return in the case that the Ember sensor is not triggered, and manual deactivation has occurred. Revisit when more sensors/resources are available to determine autonomous activation.
 *
 * Determines the appropriate duration for deactivation based on EmberSensor status.
 * When EmberSensor is on Alert (active), uses longer duration to keep human in control during crisis.
 * When EmberSensor is not on Alert, uses shorter duration to allow autonomous re-engagement.
 * @param {Array} sensors - Array of sensor objects from SystemContext
 * @param {string} deactivationType - Type of deactivation ('presoak' or 'activation')
 * @returns {string} ISO 8601 duration string
 */
export const getDeactivationDuration = (sensors, deactivationType = DEACTIVATION_TYPE.PRESOAK) => {
  // Determine which short duration to use based on deactivation type
  const shortDuration = deactivationType === DEACTIVATION_TYPE.ACTIVATION
    ? DEACTIVATION_DURATION_SHORT_ACTIVATION
    : DEACTIVATION_DURATION_SHORT_PRESOAK;

  // Find EmberSensor in sensors array
  const emberSensor = sensors?.find(s => s.id === EMBER_SENSOR_ID);

  // Edge cases: default to long duration (safe fallback)
  if (!emberSensor) {
    console.log('Deactivation: EmberSensor not found, using long duration');
    return DEACTIVATION_DURATION_SHORT_ACTIVATION;
  }

  if (emberSensor.connection?.toLowerCase() !== CONNECTION_STATUS.CONNECTED) {
    console.log('Deactivation: EmberSensor disconnected, using long duration');
    return DEACTIVATION_DURATION_SHORT_ACTIVATION;
  }

  // Check if EmberSensor is on Alert
  const isAlert = emberSensor.status?.toLowerCase() === EMBER_SENSOR_ALERT_STATUS;

  if (isAlert) {
    console.log('Deactivation: EmberSensor on Alert, using long duration for human control');
    return DEACTIVATION_DURATION_LONG;
  }

  console.log(`Deactivation (${deactivationType}): EmberSensor not on Alert, using short duration`);
  return shortDuration;
};

/**
 * Parses an ISO 8601 duration string and formats it for display as remaining time.
 * Handles formats like PT10M, PT9M30S, PT1H30M, PT876000H, etc.
 * Any seconds present will round up the minutes by 1, with overflow adjustment
 * cascading through hours, days, months, and years as needed.
 * @param {string} durationString - ISO 8601 duration string (e.g., "PT10M", "PT9M30S")
 * @returns {Object|null} Object with formatted string and indefinite flag, or null if invalid
 *   - display: formatted string for UI with units (e.g., "10Min", "2Hr30Min", "1Yr2M:15D3Hr45Min")
 *   - isIndefinite: true if total duration is 1 month or greater
 */
export const formatRemainingDuration = (durationString) => {
  if (!durationString || typeof durationString !== 'string') {
    return null;
  }

  let parsed;
  try {
    parsed = parse(durationString);
  } catch (e) {
    return null;
  }

  let years = parsed.years || 0;
  let months = parsed.months || 0;
  const weeks = parsed.weeks || 0;
  let days = (weeks * DAYS_PER_WEEK) + (parsed.days || 0);
  let hours = parsed.hours || 0;
  let minutes = parsed.minutes || 0;
  const seconds = parsed.seconds || 0;

  // Round up minutes if any seconds are present
  if (seconds > 0) {
    minutes += 1;
  }

  // Handle overflow adjustments cascading upward
  if (minutes >= MINUTES_PER_HOUR) {
    hours += Math.floor(minutes / MINUTES_PER_HOUR);
    minutes = minutes % MINUTES_PER_HOUR;
  }

  if (hours >= HOURS_PER_DAY) {
    days += Math.floor(hours / HOURS_PER_DAY);
    hours = hours % HOURS_PER_DAY;
  }

  if (days >= DAYS_PER_MONTH) {
    months += Math.floor(days / DAYS_PER_MONTH);
    days = days % DAYS_PER_MONTH;
  }

  if (months >= 12) {
    years += Math.floor(months / 12);
    months = months % 12;
  }

  // Calculate total duration in days for indefinite check (>= 1 month)
  const totalDurationInDays = (years * DAYS_PER_YEAR) + (months * DAYS_PER_MONTH) + days + (hours / HOURS_PER_DAY);
  const isIndefinite = totalDurationInDays >= DAYS_PER_MONTH;

  // Format display string: {Years}Yr{Months}M:{Days}D{Hours}Hr{Minutes}Min
  const parts = [];
  if (years > 0) parts.push(`${years} Yr `);
  if (months > 0) parts.push(`${months} Mo `);

  const timeParts = [];
  if (days > 0) timeParts.push(`${days} Day `);
  if (hours > 0) timeParts.push(`${hours} Hr `);
  if (minutes > 0) timeParts.push(`${minutes} mins `);

  let display;
  if (parts.length > 0 && timeParts.length > 0) {
    display = parts.join('') + ':' + timeParts.join('');
  } else if (parts.length > 0) {
    display = parts.join('');
  } else if (timeParts.length > 0) {
    display = timeParts.join('');
  } else {
    display = '0 mins';
  }

  return { display, isIndefinite };
};

/**
 * Formats a labeled "time remaining" string from an ISO 8601 duration, for display in
 * poll-cadence status text (as opposed to the second-by-second priming countdown).
 * @param {string} label - Text prefix describing what is remaining (e.g., "Presoaking")
 * @param {string} duration - ISO 8601 duration string to format
 * @returns {string|null} `"{label}: {display} remaining"`, or null if the duration is
 *   missing, unparseable, or indefinite (>= 1 month)
 */
export const getDurationRemainingText = (label, duration) => {
  const parsedDuration = formatRemainingDuration(duration);
  if (parsedDuration && !parsedDuration.isIndefinite && parsedDuration.display) {
    return `${label}: ${parsedDuration.display} remaining`;
  }
  return null;
};
